import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric, Skeleton, Tooltip } from '@blade/ui-kit'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'
import { Avatar, InfoMessage, Truncate } from '@root/shared/ui-kit'

import { reatomWalletActivity, walletProfileModalAtom } from '../atoms'
import { numeraireOf, valueInUsd } from '../model'

import { WalletProfileAgo } from './wallet-profile-ago'

import type { Activity } from '../atoms'
import type { WalletTransfers } from '../model'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type { Trade } from '@root/entities/trade'

/**
 * What a wallet did, in the order it did it.
 *
 * Its trades and its money movements are two lists elsewhere in this modal
 * and neither answers what this one does — money arrived and a minute later
 * the buying started. Read apart they are two lists; read together they are a
 * sequence, which is what somebody deciding whether to follow a wallet is
 * actually looking for.
 *
 * Two kinds of row share one table, so the columns are the questions both can
 * answer: when, what, in what, how much of it, and what that was worth. The
 * third column carries the token for a trade and the other wallet for a
 * transfer, which is the subject of the sentence either way.
 */

type Props = {
  walletAddress: Address
  network: Network.Network
}

export const WalletProfileActivity: React.FC<Props> = ({
  walletAddress,
  network,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomWalletActivity({ walletAddress, network }),
    [walletAddress, network],
  )
  const [state] = useAtom(model)
  const refresh = useAction(model.refresh)
  // Read once when the tab is built rather than on every render: the list is
  // a snapshot of a moment either way, and a clock read while rendering makes
  // the same tree draw differently twice.
  const now = useMemo(() => new Date(), [model])
  const [nativePrice] = useAtom(nativePriceByNetwork[network])

  const openProfile = useAction(
    (ctx, wallet: Address) =>
      walletProfileModalAtom(ctx, { walletAddress: wallet, network }),
    [network],
  )

  return (
    <Root>
      {AsyncStateN.matchWaiting(state, {
        Error: () => (
          <InfoMessage intent="error" variant="dt" onRetry={refresh} />
        ),
        Waiting: () => (
          <Rows>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} width="100%" height={22} />
            ))}
          </Rows>
        ),
        Success: (events) =>
          events.length === 0 ? (
            <InfoMessage intent="empty" variant="dt" />
          ) : (
            <Rows>
              <Head>
                <span>{t('wallet-profile.column.time')}</span>
                <span>{t('wallet-profile.column.action')}</span>
                <span>{t('wallet-profile.column.detail')}</span>
                <Right>{t('wallet-profile.column.amount')}</Right>
                <Right>
                  <Tooltip content={t('wallet-profile.column.value-note')}>
                    <Hint>{t('wallet-profile.column.value')}</Hint>
                  </Tooltip>
                </Right>
                <Right>{t('common.mcap-short')}</Right>
              </Head>
              {events.map((event) =>
                event.kind === 'trade' ? (
                  <TradeRow key={keyOf(event)} trade={event.trade} now={now} />
                ) : (
                  <TransferRow
                    key={keyOf(event)}
                    transfer={event.transfer}
                    now={now}
                    nativePriceUsd={nativePrice.toNumber()}
                    onCounterpartyClick={openProfile}
                  />
                ),
              )}
            </Rows>
          ),
      })}
    </Root>
  )
}

/** A signature is one transaction, which may hold a trade and a transfer. */
const keyOf = (event: Activity) =>
  event.kind === 'trade'
    ? `trade-${event.trade.id}`
    : `transfer-${event.transfer.signature}-${event.transfer.direction}`

const TradeRow: React.FC<{ trade: Trade.Trade; now: Date }> = ({
  trade,
  now,
}) => {
  const { t } = useTranslation()
  // The metadata arrives with the trade when the indexer had it, and the icon
  // is an atom because it can be replaced later by the logo pipeline.
  const icon = useOptionalAtom(trade.quoteCurrency?.icon)
  const ticker = trade.quoteCurrency?.ticker
  const bought = trade.type === 'buy'
  // Plenty of tokens are named after their ticker, and "Obvious Obvious" is
  // noise where the full name is meant to add something.
  const title = trade.quoteCurrency?.title
  const named = title && title.trim() !== ticker?.trim() ? title : undefined

  return (
    <Row>
      <WalletProfileAgo at={trade.timestamp.getTime()} now={now} />
      <What data-tone={bought ? 'buy' : 'sell'}>
        {t(
          bought
            ? 'wallet-profile.activity.bought'
            : 'wallet-profile.activity.sold',
        )}
      </What>
      <Subject>
        <Avatar
          size={16}
          src={icon}
          alt={ticker ?? trade.quoteCurrencyAddress}
        />
        <SubjectName>
          {ticker ?? (
            <Truncate start={4} end={4}>
              {trade.quoteCurrencyAddress}
            </Truncate>
          )}
        </SubjectName>
        {named ? <SubjectAside>{named}</SubjectAside> : undefined}
      </Subject>
      <Right>
        <Numeric value={trade.currencyAmount} preset="currency-amount-short" />
      </Right>
      <Right data-tone={bought ? 'buy' : 'sell'}>
        <Numeric
          value={trade.totalUsd}
          preset="currency-amount-short"
          currency="$"
        />
      </Right>
      <Right>
        {trade.marketCapUsd.isGreaterThan(0) ? (
          <Numeric
            value={trade.marketCapUsd}
            preset="currency-amount-short"
            currency="$"
          />
        ) : (
          <Unknown>—</Unknown>
        )}
      </Right>
    </Row>
  )
}

const TransferRow: React.FC<{
  transfer: WalletTransfers.Transfer
  now: Date
  nativePriceUsd: number
  onCounterpartyClick: (wallet: Address) => void
}> = ({ transfer, now, nativePriceUsd, onCounterpartyClick }) => {
  const { t } = useTranslation()
  const asset = numeraireOf(transfer.mint)
  const worth = valueInUsd(transfer.mint, transfer.amount, nativePriceUsd)
  const arrived = transfer.direction === 'in'

  return (
    <Row>
      <WalletProfileAgo at={transfer.at} now={now} />
      <What data-tone={arrived ? 'in' : 'out'}>
        {t(
          arrived
            ? 'wallet-profile.transfers.in'
            : 'wallet-profile.transfers.out',
        )}
      </What>
      <Subject>
        <Avatar size={16} src={asset.logo} alt={asset.ticker} />
        <SubjectName>{asset.ticker}</SubjectName>
        {/* The other wallet is a wallet we have a profile for, so it opens
            one rather than being eight characters to copy out. */}
        <SubjectLink
          type="button"
          onClick={() => onCounterpartyClick(transfer.counterparty as Address)}
        >
          {arrived ? '←' : '→'}{' '}
          <Truncate start={4} end={4}>
            {transfer.counterparty}
          </Truncate>
        </SubjectLink>
      </Subject>
      <Right>
        <Numeric value={transfer.amount} preset="currency-amount-short" />
      </Right>
      <Right data-tone={arrived ? 'in' : 'out'}>
        {worth === undefined ? (
          <Unknown>—</Unknown>
        ) : (
          <Numeric value={worth} preset="currency-amount-short" currency="$" />
        )}
      </Right>
      <Right>
        <Unknown>—</Unknown>
      </Right>
    </Row>
  )
}

const COLUMNS = `48px 52px minmax(96px, 1fr) minmax(72px, 96px) minmax(68px, 88px) minmax(64px, 80px)`

const Root = styled.div`
  overflow: auto;
  display: grid;
  align-content: start;
`

const Rows = styled.div`
  display: grid;
  gap: 2px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 12px;
  align-items: center;

  padding: 5px 6px;
  border-radius: 6px;

  color: var(--text-10);
  font-size: 11px;
  font-variant-numeric: tabular-nums;

  &:hover {
    background: var(--appearanceA-1);
  }

  /* A narrow screen keeps when, what, in what, and what it was worth. The
     market cap goes first because it is context; the asset amount goes with
     it because the dollar figure answers the same question in one unit. */
  @media ${({ theme }) => theme.tablet.queryMaxWidth} {
    grid-template-columns: 44px 48px minmax(80px, 1fr) minmax(68px, 88px);
    gap: 8px;

    & > :nth-child(4),
    & > :nth-child(6) {
      display: none;
    }
  }
`

/** Stays put while the rows scroll; a table without headings is a wall. */
const Head = styled(Row)`
  position: sticky;
  top: 0;
  z-index: 1;

  background: var(--background-1);
  color: var(--text-9);
  font-size: 10px;

  &:hover {
    background: var(--background-1);
  }
`

const What = styled.span`
  font-size: 10px;
  font-weight: 500;

  &[data-tone='buy'],
  &[data-tone='in'] {
    color: var(--up-9);
  }

  &[data-tone='sell'],
  &[data-tone='out'] {
    color: var(--down-9);
  }
`

const Subject = styled.div`
  overflow: hidden;
  display: flex;
  gap: 6px;
  align-items: center;
`

const SubjectName = styled.span`
  flex: none;

  color: var(--text-11);
  font-weight: 500;
`

/** The full name or the other wallet: the first thing to go when narrow. */
const SubjectAside = styled.span`
  overflow: hidden;

  color: var(--text-9);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SubjectLink = styled.button`
  overflow: hidden;
  padding: 0;
  border: none;
  background: none;

  color: var(--text-9);
  font: inherit;
  font-size: 10px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--text-12);
    text-decoration: underline;
  }
`

const Right = styled.div`
  text-align: right;

  &[data-tone='buy'],
  &[data-tone='in'] {
    color: var(--up-9);
  }

  &[data-tone='sell'],
  &[data-tone='out'] {
    color: var(--down-9);
  }
`

const Hint = styled.span`
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: help;
`

const Unknown = styled.span`
  color: var(--text-9);
`
